/**
 * Setup Account Page
 * 
 * Used after OTP verification to set up email (username) and password
 * Flows into Intent Wizard and then Tax Registration
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Lock, Eye, EyeOff, CheckCircle, Shield, Mail, User, ArrowRight, Loader2, Phone, Globe } from 'lucide-react';
import IntentWizard from '../components/onboarding/IntentWizard';

const COUNTRY_CODES = [
  // South Asia
  { code: '91', label: 'IN +91', flag: '🇮🇳' },
  { code: '92', label: 'PK +92', flag: '🇵🇰' },
  { code: '880', label: 'BD +880', flag: '🇧🇩' },
  { code: '94', label: 'LK +94', flag: '🇱🇰' },
  { code: '977', label: 'NP +977', flag: '🇳🇵' },
  // Middle East
  { code: '971', label: 'AE +971', flag: '🇦🇪' },
  { code: '966', label: 'SA +966', flag: '🇸🇦' },
  { code: '974', label: 'QA +974', flag: '🇶🇦' },
  { code: '973', label: 'BH +973', flag: '🇧🇭' },
  { code: '968', label: 'OM +968', flag: '🇴🇲' },
  { code: '965', label: 'KW +965', flag: '🇰🇼' },
  { code: '962', label: 'JO +962', flag: '🇯🇴' },
  { code: '972', label: 'IL +972', flag: '🇮🇱' },
  { code: '90', label: 'TR +90', flag: '🇹🇷' },
  // Europe
  { code: '44', label: 'UK +44', flag: '🇬🇧' },
  { code: '49', label: 'DE +49', flag: '🇩🇪' },
  { code: '33', label: 'FR +33', flag: '🇫🇷' },
  { code: '39', label: 'IT +39', flag: '🇮🇹' },
  { code: '34', label: 'ES +34', flag: '🇪🇸' },
  { code: '31', label: 'NL +31', flag: '🇳🇱' },
  { code: '46', label: 'SE +46', flag: '🇸🇪' },
  { code: '47', label: 'NO +47', flag: '🇳🇴' },
  { code: '45', label: 'DK +45', flag: '🇩🇰' },
  { code: '48', label: 'PL +48', flag: '🇵🇱' },
  { code: '380', label: 'UA +380', flag: '🇺🇦' },
  { code: '41', label: 'CH +41', flag: '🇨🇭' },
  { code: '43', label: 'AT +43', flag: '🇦🇹' },
  { code: '32', label: 'BE +32', flag: '🇧🇪' },
  { code: '353', label: 'IE +353', flag: '🇮🇪' },
  { code: '351', label: 'PT +351', flag: '🇵🇹' },
  { code: '30', label: 'GR +30', flag: '🇬🇷' },
  { code: '420', label: 'CZ +420', flag: '🇨🇿' },
  { code: '40', label: 'RO +40', flag: '🇷🇴' },
  { code: '36', label: 'HU +36', flag: '🇭🇺' },
  { code: '358', label: 'FI +358', flag: '🇫🇮' },
  // North America
  { code: '1', label: 'US +1', flag: '🇺🇸' },
  { code: '1CA', label: 'CA +1', flag: '🇨🇦' },
  { code: '52', label: 'MX +52', flag: '🇲🇽' },
  // South America
  { code: '55', label: 'BR +55', flag: '🇧🇷' },
  { code: '54', label: 'AR +54', flag: '🇦🇷' },
  { code: '56', label: 'CL +56', flag: '🇨🇱' },
  { code: '57', label: 'CO +57', flag: '🇨🇴' },
  { code: '51', label: 'PE +51', flag: '🇵🇪' },
  // JAPAC (Japan, Asia-Pacific)
  { code: '81', label: 'JP +81', flag: '🇯🇵' },
  { code: '86', label: 'CN +86', flag: '🇨🇳' },
  { code: '82', label: 'KR +82', flag: '🇰🇷' },
  { code: '65', label: 'SG +65', flag: '🇸🇬' },
  { code: '60', label: 'MY +60', flag: '🇲🇾' },
  { code: '66', label: 'TH +66', flag: '🇹🇭' },
  { code: '84', label: 'VN +84', flag: '🇻🇳' },
  { code: '62', label: 'ID +62', flag: '🇮🇩' },
  { code: '63', label: 'PH +63', flag: '🇵🇭' },
  { code: '61', label: 'AU +61', flag: '🇦🇺' },
  { code: '64', label: 'NZ +64', flag: '🇳🇿' },
  { code: '852', label: 'HK +852', flag: '🇭🇰' },
  { code: '886', label: 'TW +886', flag: '🇹🇼' },
  // Africa
  { code: '27', label: 'ZA +27', flag: '🇿🇦' },
  { code: '234', label: 'NG +234', flag: '🇳🇬' },
  { code: '254', label: 'KE +254', flag: '🇰🇪' },
  { code: '20', label: 'EG +20', flag: '🇪🇬' },
];

const SetupAccount = () => {
  const [step, setStep] = useState('credentials'); // 'credentials' | 'wizard' | 'complete'
  
  // Form state
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Mobile number for email-signup users
  const [mobileNumber, setMobileNumber] = useState('');
  const [countryCode, setCountryCode] = useState('91');
  const [mobileError, setMobileError] = useState('');
  
  // Verified data from registration
  const [verifiedMobile, setVerifiedMobile] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [verificationMethod, setVerificationMethod] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  const { login, apiCall } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Registration state storage key & helpers
  const REG_STORAGE_KEY = 'aniyor_reg_state';
  const REG_EXPIRY_MS = 60 * 60 * 1000; // 60 minutes

  const getRegState = () => {
    try {
      const raw = localStorage.getItem(REG_STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      // Auto-clear stale data (older than 60 minutes)
      if (Date.now() - (data.timestamp || 0) > REG_EXPIRY_MS) {
        localStorage.removeItem(REG_STORAGE_KEY);
        return null;
      }
      return data;
    } catch {
      localStorage.removeItem(REG_STORAGE_KEY);
      return null;
    }
  };

  const updateRegState = (patch) => {
    const current = getRegState() || { timestamp: Date.now() };
    const updated = { ...current, ...patch, timestamp: Date.now() };
    localStorage.setItem(REG_STORAGE_KEY, JSON.stringify(updated));
  };

  const clearRegState = () => {
    localStorage.removeItem(REG_STORAGE_KEY);
  };
  
  // Name validation - minimum 3 characters
  const validateName = (nameValue) => {
    if (!nameValue || nameValue.trim().length === 0) {
      return { valid: false, error: '' };
    }
    if (nameValue.trim().length < 3) {
      return { valid: false, error: 'Name must be at least 3 characters' };
    }
    if (nameValue.trim().length > 100) {
      return { valid: false, error: 'Name must be less than 100 characters' };
    }
    return { valid: true, error: '' };
  };
  
  // Email validation helper
  const validateEmail = (emailValue) => {
    if (!emailValue) return { valid: false, error: '' };
    
    const basicRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!basicRegex.test(emailValue)) {
      return { valid: false, error: 'Please enter a valid email address' };
    }
    
    const domain = emailValue.split('@')[1];
    if (!domain) return { valid: false, error: 'Invalid email format' };
    
    const domainParts = domain.split('.');
    const tld = domainParts[domainParts.length - 1];
    if (tld.length < 2) {
      return { valid: false, error: 'Please enter a complete email address' };
    }
    
    // Check for common typos
    const suspiciousPatterns = [
      { pattern: /^gma\.com$/i, suggestion: 'gmail.com' },
      { pattern: /^gmial\.com$/i, suggestion: 'gmail.com' },
      { pattern: /^gmai\.com$/i, suggestion: 'gmail.com' },
      { pattern: /^gamil\.com$/i, suggestion: 'gmail.com' },
    ];
    
    for (const { pattern, suggestion } of suspiciousPatterns) {
      if (pattern.test(domain)) {
        return { valid: false, error: `Did you mean ${suggestion}?` };
      }
    }
    
    return { valid: true, error: '' };
  };
  
  const isNameValid = validateName(name).valid;
  
  // Check for verified data on mount (hydrate from localStorage)
  useEffect(() => {
    const regState = getRegState();

    if (!regState || (!regState.verified_mobile && !regState.verified_email)) {
      // No valid verification found — redirect to register
      toast({
        title: 'Verification Required',
        description: 'Please verify your number to continue your registration.',
        variant: 'destructive',
      });
      navigate('/register');
      return;
    }

    setVerifiedMobile(regState.verified_mobile || '');
    setVerifiedEmail(regState.verified_email || '');
    setVerificationMethod(regState.verification_method || '');

    // Pre-fill email if verified via email
    if (regState.verified_email) {
      setEmail(regState.verified_email);
    }

    // Hydrate wizard step if it was previously saved
    if (regState.last_step === 'wizard') {
      setStep('wizard');
    }
    if (regState.saved_name) setName(regState.saved_name);
    if (regState.saved_email && !regState.verified_email) setEmail(regState.saved_email);
  }, [navigate, toast]);
  
  // Password validation
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    match: password === confirmPassword && password.length > 0
  };
  
  const isValidPassword = Object.values(passwordChecks).every(Boolean);
  
  // Check if email is valid using proper validation
  const isValidEmail = validateEmail(email).valid;
  
  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    
    // Validate name
    const nameValidation = validateName(name);
    if (!nameValidation.valid) {
      const errorMsg = nameValidation.error || 'Please enter your name (minimum 3 characters)';
      setNameError(errorMsg);
      toast({ title: 'Error', description: errorMsg, variant: 'destructive' });
      return;
    }
    setNameError('');
    
    // Validate email
    const emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      const errorMsg = emailValidation.error || 'Please enter a valid email address';
      setEmailError(errorMsg);
      toast({ title: 'Error', description: errorMsg, variant: 'destructive' });
      return;
    }
    setEmailError('');
    
    if (!isValidPassword) {
      toast({ title: 'Error', description: 'Please ensure all password requirements are met', variant: 'destructive' });
      return;
    }
    
    // Check if email is already taken (if different from verified email)
    if (email !== verifiedEmail) {
      setLoading(true);
      try {
        const result = await apiCall('/api/auth/check-email', {
          method: 'POST',
          body: JSON.stringify({ email })
        });
        
        if (result.exists) {
          toast({ 
            title: 'Email Already Registered', 
            description: 'This email is already associated with an account. Please use a different email or login.',
            variant: 'destructive'
          });
          setLoading(false);
          return;
        }
      } catch (error) {
        toast({ title: 'Error', description: error.message, variant: 'destructive' });
        setLoading(false);
        return;
      }
      setLoading(false);
    }
    
    // Move to wizard step — persist progress
    updateRegState({ last_step: 'wizard', saved_name: name, saved_email: email });
    setStep('wizard');
  };
  
  const handleWizardComplete = async (wizardData) => {
    setLoading(true);
    
    // Get international status from registration state
    const regState = getRegState();
    const isIntl = regState?.is_international || false;
    const hasIndianPan = regState?.has_indian_pan ?? true; // Default true for Indian users
    const isLeadsOnly = isIntl && !hasIndianPan;
    
    try {
      const result = await apiCall('/api/auth/register-vendor', {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          mobile: verifiedMobile || (mobileNumber ? `${mobileNumber}` : ''),
          country_code: verifiedMobile ? countryCode : countryCode.replace('CA', ''),
          password: password,
          name: name,
          onboarding_profile: wizardData,
          is_international: isIntl,
          has_indian_pan: hasIndianPan,
          account_type: isLeadsOnly ? 'leads_only' : 'full',
          terms_accepted: termsAccepted
        })
      });
      
      // Clear registration state from localStorage
      clearRegState();
      
      // Login the user
      login(result.user, result.access_token);
      
      if (isLeadsOnly) {
        setStep('complete_international');
      } else {
        setStep('complete');
      }
      
      toast({ title: 'Welcome!', description: 'Account created successfully!' });
      
      // Redirect: International leads-only users skip tax registration → go to subscription
      // Indian/PAN users → tax registration
      setTimeout(() => {
        if (isLeadsOnly) {
          navigate('/vendor/subscription/plans');
        } else {
          navigate('/vendor/tax-registration');
        }
      }, 3000);
      
    } catch (error) {
      toast({ title: 'Registration Failed', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  
  // Success screen — International Lead Gen account
  if (step === 'complete_international') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
        <Card className="w-full max-w-md" data-testid="international-welcome-screen">
          <CardContent className="pt-8 pb-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="h-8 w-8 text-indigo-600" />
              </div>
              <h2 className="text-xl font-bold mb-1">Welcome to Aniyor!</h2>
              <p className="text-sm text-gray-500">Your Lead Generation Account is Active.</p>
            </div>
            
            <div className="bg-white border rounded-lg p-4 mb-4">
              <p className="text-xs text-gray-500 mb-3">Since you've registered without an Indian PAN, we've optimized your experience for Lead Discovery and Management.</p>
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b"><td className="py-2 text-gray-600">Lead Discovery</td><td className="py-2 text-right font-medium text-green-600">Fully Active</td></tr>
                  <tr className="border-b"><td className="py-2 text-gray-600">CRM & Prospecting Tools</td><td className="py-2 text-right font-medium text-green-600">Fully Active</td></tr>
                  <tr className="border-b"><td className="py-2 text-gray-600">Client Communication</td><td className="py-2 text-right font-medium text-green-600">Fully Active</td></tr>
                  <tr><td className="py-2 text-gray-600">Transactional Payments</td><td className="py-2 text-right font-medium text-amber-600">Coming Soon</td></tr>
                </tbody>
              </table>
            </div>
            
            <p className="text-xs text-gray-400 text-center mb-4">
              Full transactional capabilities will be available once we are compliant with your country's tax laws.
            </p>
            
            <p className="text-xs text-gray-500 text-center">Redirecting to choose your plan...</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Success screen — Standard Indian/PAN account
  if (step === 'complete') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Account Created!</h2>
            <p className="text-gray-500">Redirecting to complete your registration...</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Wizard step
  if (step === 'wizard') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <CardTitle>Tell Us About Your Business</CardTitle>
            <CardDescription>
              Help us customize your experience
            </CardDescription>
          </CardHeader>
          <CardContent>
            <IntentWizard 
              onComplete={handleWizardComplete} 
              isInternational={getRegState()?.is_international && !getRegState()?.has_indian_pan}
            />
            
            {loading && (
              <div className="flex items-center justify-center py-4 mt-4">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">Creating your account...</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Credentials step (default)
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-blue-600" />
          </div>
          <CardTitle>Set Up Your Account</CardTitle>
          <CardDescription>
            Create your login credentials
          </CardDescription>
          {verifiedMobile && (
            <p className="text-sm text-green-600 mt-2">
              ✓ Mobile verified: +91 {verifiedMobile}
            </p>
          )}
          {verifiedEmail && verificationMethod === 'email' && (
            <p className="text-sm text-green-600 mt-2">
              ✓ Email verified: {verifiedEmail}
            </p>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCredentialsSubmit} className="space-y-4">
            {/* Full Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2">
                <User className="h-4 w-4" /> Full Name <span className="text-xs text-gray-400">(min. 3 characters)</span>
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => {
                  const newName = e.target.value;
                  setName(newName);
                  if (newName) {
                    const validation = validateName(newName);
                    setNameError(validation.error);
                  } else {
                    setNameError('');
                  }
                }}
                placeholder="Enter your full name"
                className={nameError ? 'border-red-500 focus:ring-red-500' : ''}
              />
              {nameError && (
                <p className="text-sm text-red-500">{nameError}</p>
              )}
            </div>
            
            {/* Email (Username) */}
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" /> Email (Username)
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  const newEmail = e.target.value;
                  setEmail(newEmail);
                  if (newEmail) {
                    const validation = validateEmail(newEmail);
                    setEmailError(validation.error);
                  } else {
                    setEmailError('');
                  }
                }}
                placeholder="you@example.com"
                disabled={verificationMethod === 'email'}
                className={emailError ? 'border-red-500 focus:ring-red-500' : ''}
              />
              {emailError && (
                <p className="text-sm text-red-500">{emailError}</p>
              )}
              {verificationMethod === 'email' && (
                <p className="text-xs text-gray-500">Email pre-filled from verification</p>
              )}
            </div>
            
            {/* Mobile Number (for email-signup users only) */}
            {verificationMethod === 'email' && (
              <div className="space-y-2">
                <Label htmlFor="mobile" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" /> Mobile Number <span className="text-xs text-gray-400">(optional)</span>
                </Label>
                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="h-9 rounded-md border border-input bg-transparent px-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring w-[110px]"
                    data-testid="country-code-select"
                  >
                    {COUNTRY_CODES.map(cc => (
                      <option key={cc.code} value={cc.code}>
                        {cc.flag} {cc.label}
                      </option>
                    ))}
                  </select>
                  <Input
                    id="mobile"
                    type="tel"
                    value={mobileNumber}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 15);
                      setMobileNumber(val);
                      setMobileError('');
                    }}
                    placeholder="Mobile number"
                    className={`flex-1 ${mobileError ? 'border-red-500 focus:ring-red-500' : ''}`}
                    data-testid="mobile-number-input"
                  />
                </div>
                {mobileError && (
                  <p className="text-sm text-red-500">{mobileError}</p>
                )}
              </div>
            )}
            
            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center gap-2">
                <Lock className="h-4 w-4" /> Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a strong password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            {/* Password Requirements */}
            <div className="bg-gray-50 p-3 rounded-lg border">
              <p className="text-xs font-medium text-gray-600 mb-2">Password Requirements:</p>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <div className={`flex items-center gap-1 ${passwordChecks.length ? 'text-green-600' : 'text-gray-400'}`}>
                  <CheckCircle size={12} /> 8+ characters
                </div>
                <div className={`flex items-center gap-1 ${passwordChecks.uppercase ? 'text-green-600' : 'text-gray-400'}`}>
                  <CheckCircle size={12} /> Uppercase letter
                </div>
                <div className={`flex items-center gap-1 ${passwordChecks.lowercase ? 'text-green-600' : 'text-gray-400'}`}>
                  <CheckCircle size={12} /> Lowercase letter
                </div>
                <div className={`flex items-center gap-1 ${passwordChecks.number ? 'text-green-600' : 'text-gray-400'}`}>
                  <CheckCircle size={12} /> Number
                </div>
                <div className={`flex items-center gap-1 col-span-2 ${passwordChecks.match ? 'text-green-600' : 'text-gray-400'}`}>
                  <CheckCircle size={12} /> Passwords match
                </div>
              </div>
            </div>
            
            <div className="mt-4">
              <label className="flex items-start space-x-3 cursor-pointer">
                <Checkbox
                  checked={termsAccepted}
                  onCheckedChange={(checked) => setTermsAccepted(checked)}
                  data-testid="registration-terms-checkbox"
                  className="mt-0.5"
                />
                <span className="text-sm text-gray-600">
                  I agree to the{' '}
                  <a href="https://docs.google.com/document/d/e/2PACX-1vS-Ej4rVtNwVy4nIp8aGJvp6jZMt5uV0mZkQJlpkqd_m/pub" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">
                    Seller Agreement
                  </a>
                  {' '}and{' '}
                  <a href="https://docs.google.com/document/d/e/2PACX-1vTlYT3VQ1hYy1k-CPZP4WDPZQFQJMZZ3T1x2A/pub" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">
                    Privacy Policy
                  </a>
                </span>
              </label>
            </div>
            
            <div className="sticky bottom-0 z-10 bg-white border-t border-gray-200 -mx-6 px-6 py-3 mt-4 md:relative md:border-t-0 md:bg-transparent md:mx-0 md:px-0 md:py-0 md:mt-0">
              <Button 
                type="submit" 
                className="w-full"
                disabled={loading || !isNameValid || !isValidEmail || !isValidPassword || !termsAccepted}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <>
                    Continue <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SetupAccount;
